import React, { useState } from 'react';
import { AlertTriangle, Mail, Lock } from 'lucide-react';
import Modal from '../Modal';
import { PIPELINE, UPGRADE_STATUS, TRANSITION_UI } from '../../constants/upgrades';
import { czDate, czDateTime } from '../../utils/upgradesApi';

const TONE_ORDER = { primary: 0, secondary: 1, danger: 2 };

// Messages for the server-computed attention flags (with a hint what to do)
function flagBanners(job) {
  const out = [];
  const mailto = job.contact_email
    ? `mailto:${encodeURIComponent(job.contact_email)}?subject=${encodeURIComponent(`Cenová nabídka ${job.number}`)}&body=${encodeURIComponent('Dobrý den,\n\nrádi bychom se zeptali, zda jste měli čas se podívat na naši cenovou nabídku. V případě dotazů jsme k dispozici.\n\nS pozdravem')}`
    : null;
  if (job.flags.includes('quote_expired')) out.push({ key: 'exp', red: true, text: `Nabídce vypršela platnost (${czDate(job.quote_valid_until)}). Prodlužte platnost, nebo úpravu zrušte.` });
  if (job.flags.includes('needs_followup')) out.push({ key: 'fu', text: `Nabídka je odeslaná od ${czDate(job.quote_sent_at)} a zákazník se neozval.`, mailto });
  if (job.flags.includes('unscheduled')) out.push({ key: 'un', text: 'Úprava je schválená – vyberte termín a posádku, pak se sama přepne na „Naplánováno“.' });
  if (job.flags.includes('overdue')) out.push({ key: 'od', red: true, text: 'Termín výjezdu už uplynul. Upravte termín, nebo zahajte práce.' });
  if (job.flags.includes('starts_soon')) out.push({ key: 'ss', text: `Brzy se jede (${czDate(job.planned_start)}) – zkontrolujte díly a vytiskněte výjezdový list.` });
  return out;
}

// Stepper, attention banners and the buttons that move the job to the next status.
const UpgradeStatusBar = ({ job, dirty, busy, onTransition }) => {
  const [prompt, setPrompt] = useState(null);
  const [text, setText] = useState('');
  const idx = PIPELINE.indexOf(job.status);
  const sorted = [...job.transitions].sort((a, b) => (TONE_ORDER[(TRANSITION_UI[`${job.status}>${a.to}`] || [])[1]] ?? 1) - (TONE_ORDER[(TRANSITION_UI[`${job.status}>${b.to}`] || [])[1]] ?? 1));
  const primary = sorted.find((t) => (TRANSITION_UI[`${job.status}>${t.to}`] || [])[1] === 'primary');
  const blockers = (t) => t.problems.filter((p) => !p.forceable);

  const click = (t) => {
    setText('');
    if (t.to === 'schvaleno' && job.status !== 'naplanovano') setPrompt({ to: t.to, kind: 'approve' });
    else if (t.to === 'zruseno') setPrompt({ to: t.to, kind: 'cancel' });
    else if (t.problems.some((p) => p.forceable)) setPrompt({ to: t.to, kind: 'force', message: t.problems.find((p) => p.forceable).message });
    else onTransition(t.to);
  };
  const confirm = () => {
    const { to, kind } = prompt;
    setPrompt(null);
    if (kind === 'approve') onTransition(to, { approved_by_name: text.trim() || undefined });
    else if (kind === 'cancel') onTransition(to, { cancel_reason: text.trim() || undefined });
    else onTransition(to, { force: true });
  };

  return (
    <section aria-label="Stav úpravy">
      <ol className="upg-steps" style={{ listStyle: 'none', padding: 0 }}>
        {PIPELINE.map((s, i) => (
          <li key={s} className={`upg-steps__item ${job.status === 'zruseno' ? '' : i < idx ? 'upg-steps__item--done' : i === idx ? 'upg-steps__item--now' : ''}`}
            aria-current={job.status === s ? 'step' : undefined}>{UPGRADE_STATUS[s].short}</li>
        ))}
      </ol>

      <div style={{ marginTop: 14 }}>
        {flagBanners(job).map((b) => (
          <div key={b.key} className={`upg-banner ${b.red ? 'upg-banner--red' : ''}`} role="status">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>{b.text}{b.mailto && <> <a className="upg-link" href={b.mailto}><Mail size={13} style={{ verticalAlign: '-2px' }} /> Připomenout e-mailem</a></>}</span>
          </div>
        ))}
        {job.status === 'zruseno' && job.cancel_reason && <div className="upg-banner upg-banner--red"><Lock size={16} /> <span>Zrušeno: {job.cancel_reason}</span></div>}
        {job.approved_at && <p className="upg-muted" style={{ fontSize: 13, margin: '0 0 8px' }}>Schváleno {czDateTime(job.approved_at)}{job.approved_by_name ? ` (${job.approved_by_name})` : ''}.</p>}
      </div>

      <div className="upg-actions">
        {sorted.map((t) => {
          const [label, tone] = TRANSITION_UI[`${job.status}>${t.to}`] || [UPGRADE_STATUS[t.to].label, 'secondary'];
          const blocked = blockers(t).length > 0;
          return (
            <button key={t.to} type="button" disabled={busy || dirty || blocked}
              className={`dlv-btn ${tone === 'primary' ? 'dlv-btn--primary' : ''} ${tone === 'danger' ? 'upg-btn-danger' : ''}`}
              title={dirty ? 'Nejdřív uložte změny' : blocked ? blockers(t).map((p) => p.message).join(' · ') : undefined} onClick={() => click(t)}>
              {label}
            </button>
          );
        })}
      </div>
      {primary && blockers(primary).length > 0 && (
        <ul className="upg-problems" aria-label="Co ještě chybí">
          {blockers(primary).map((p) => <li key={p.code}>{p.message}</li>)}
        </ul>
      )}
      {dirty && <p className="upg-muted" style={{ fontSize: 13, margin: '8px 0 0' }}>Změna stavu je možná až po uložení úprav.</p>}

      <Modal show={Boolean(prompt)} onClose={() => setPrompt(null)}>
        {prompt && (
          <div className="dlv dlv-card" role="dialog" aria-modal="true" aria-labelledby="upg-prompt-title" style={{ width: 'min(480px, calc(100vw - 32px))' }}>
            <h2 id="upg-prompt-title" className="dlv-section-head__title" style={{ fontSize: 18, marginBottom: 10 }}>
              {prompt.kind === 'approve' ? 'Zákazník schválil nabídku' : prompt.kind === 'cancel' ? 'Zrušit úpravu' : 'Dokončit i přesto?'}
            </h2>
            {prompt.kind === 'force' && <p>{prompt.message}. Neodškrtnuté změny se nepovažují za provedené a funkce trenažéru se k nim nenastaví.</p>}
            {prompt.kind !== 'force' && (
              <>
                <label className="dlv-label" htmlFor="upg-prompt-text">{prompt.kind === 'approve' ? 'Kdo za zákazníka schválil (nepovinné)' : 'Důvod zrušení (nepovinné)'}</label>
                {prompt.kind === 'approve'
                  ? <input id="upg-prompt-text" className="dlv-input" autoFocus value={text} maxLength={255} onChange={(e) => setText(e.target.value)} placeholder="Např. Jana Nováková, e-mailem" />
                  : <textarea id="upg-prompt-text" className="dlv-input upg-textarea" autoFocus value={text} maxLength={2000} onChange={(e) => setText(e.target.value)} />}
              </>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="dlv-btn" onClick={() => setPrompt(null)}>Zpět</button>
              <button type="button" className={`dlv-btn ${prompt.kind === 'cancel' ? 'dlv-btn--dark' : 'dlv-btn--primary'}`} onClick={confirm}>
                {prompt.kind === 'cancel' ? 'Zrušit úpravu' : prompt.kind === 'force' ? 'Dokončit' : 'Potvrdit schválení'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default UpgradeStatusBar;
